import React, { useState, useEffect } from 'react';
import FaiBox from "../../../../../MuiComponents/FaiBox";
import FaiTypography from "../../../../../MuiComponents/FaiTypography";
import FaiButton from "../../../../../MuiComponents/FaiButton";
import theme from "../../../../../assets/theme";
import DataTable from "../../../../../components/DataTable";
import SalesController from '../../../../../controllers/saleController';
import { Sale } from '../../../../../types/orderTypes';
import { PaginatedResponse } from '../../../../../types/salesTypes';
import { Switch, IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Snackbar, Alert } from '@mui/material';
import EditIcon from '../../../../../assets/images/icons/edit.svg';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditSaleModal from './EditSaleModal';
import MediaUploadModal from '../../../../../components/MediaUploadModal';

function OngoingSales() {
    const { typography, palette, borders } = theme;
    interface FormattedSale {
        id: string;
        brand: string;
        product: string;
        discount: string;
        startDate: string;
        endDate: string;
        status: string;
        edit: string;
    }
    const [sales, setSales] = useState<FormattedSale[]>([]);
    const [loading, setLoading] = useState(true);
    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    const [selectedSaleId, setSelectedSaleId] = useState<string | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [saleToDelete, setSaleToDelete] = useState<string | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
    const [statusUpdating, setStatusUpdating] = useState<{[key: string]: boolean}>({});
    const [mediaUploadOpen, setMediaUploadOpen] = useState(false);
    const [selectedSaleForMedia, setSelectedSaleForMedia] = useState<string | null>(null);

    const fetchSales = async () => {
        try {
            setLoading(true);
            const response = await SalesController.getSales();
            console.log('Sales Data:', response.data);

            if (response.data?.data?.data) {
                const collectNames = (arr: any) =>
                    Array.isArray(arr)
                        ? arr.map((x: any) => x?.name || x?.brand || '').filter(Boolean).join(', ')
                        : '';

                const formattedSales = response.data.data.data.map((sale: any) => ({
                    id: sale.id,
                    campaign: sale.campaignName || '-',
                    brand: sale.brand,
                    type: sale.saleType || '-',
                    // A sale can target products, sub-categories, or master categories — show
                    // whichever the backend populated (the others come back null).
                    product:
                        collectNames(sale.products) ||
                        collectNames(sale.categories) ||
                        collectNames(sale.masterCategories),
                    // Flat shows ₹, percentage shows %.
                    discount:
                        sale.saleType === 'FLAT'
                            ? `₹${sale.percentageFlat}`
                            : `${sale.percentageFlat}%`,
                    startDate: new Date(sale.validFromDateTime).toLocaleDateString(),
                    endDate: new Date(sale.validTillDateTime).toLocaleDateString(),
                    status: sale.status,
                    edit: "edit"
                }));

                setSales(formattedSales);
            }
        } catch (error) {
            console.error('Error fetching sales:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSales();
    }, []);

    const handleEditClick = (saleId: string) => {
        console.log('Edit clicked for sale ID:', saleId);
        setSelectedSaleId(saleId);
        setEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setSelectedSaleId(null);
    };

    const handleSaleUpdated = () => {
        // Refresh the sales list after update
        fetchSales();
    };

    const handleDeleteClick = (saleId: string) => {
        setSaleToDelete(saleId);
        setDeleteConfirmOpen(true);
    };

    const handleCloseDeleteConfirm = () => {
        setDeleteConfirmOpen(false);
        setSaleToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!saleToDelete) return;
        
        setDeleteLoading(true);
        try {
            await SalesController.deleteSale(saleToDelete);
            setSnackbarMessage('Sale deleted successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            fetchSales();
        } catch (error) {
            setSnackbarMessage('Failed to delete sale. Please try again.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setDeleteLoading(false);
            setDeleteConfirmOpen(false);
            setSaleToDelete(null);
        }
    };

    const handleAddMedia = (saleId: string) => {
        setSelectedSaleForMedia(saleId);
        setMediaUploadOpen(true);
    };

    const handleMediaUploadSuccess = () => {
        setSnackbarMessage('Media uploaded successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchSales();
    };

    const handleCloseMediaUpload = () => {
        setMediaUploadOpen(false);
        setSelectedSaleForMedia(null);
    };

    const handleDeleteMedia = async (saleId: string) => {
        try {
            await SalesController.deleteSaleMedia(saleId, 'mediaKey'); // Replace 'mediaKey' with actual media key
            setSnackbarMessage('Media deleted successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            setSnackbarMessage('Failed to delete media. Please try again.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    const handleStatusChange = async (saleId: string, currentStatus: string) => {
        try {
            setStatusUpdating(prev => ({ ...prev, [saleId]: true }));
            
            const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
            await SalesController.updateSaleStatus(saleId, { status: newStatus });
            
            setSnackbarMessage(`Sale status updated to ${newStatus.toLowerCase()}`);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            
            // Update the local state
            setSales(prevSales => 
                prevSales.map(sale => 
                    sale.id === saleId 
                        ? { ...sale, status: newStatus } 
                        : sale
                )
            );
        } catch (error) {
            setSnackbarMessage('Failed to update sale status. Please try again.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setStatusUpdating(prev => ({ ...prev, [saleId]: false }));
        }
    };

    const tableData = {
        columns: [
            { Header: "Campaign", accessor: "campaign", width: "18%" },
            { Header: "Brand", accessor: "brand" },
            { Header: "Type", accessor: "type" },
            { Header: "Product", accessor: "product" },
            { Header: "Discount", accessor: "discount" },
            { Header: "Start Date", accessor: "startDate" },
            { Header: "End Date", accessor: "endDate" },
            {
                Header: "Status",
                accessor: "status",
                Cell: ({ value, row }: { value: string, row: any }) => (
                    <Switch 
                        checked={value === 'ACTIVE'} 
                        color="success" 
                        disabled={statusUpdating[row.original.id] || false}
                        onChange={() => handleStatusChange(row.original.id, value)}
                    />
                )
            },
            {
                Header: "Actions",
                accessor: "actions",
                Cell: ({ row }: any) => {
                    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
                    const open = Boolean(anchorEl);

                    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
                        setAnchorEl(event.currentTarget);
                    };

                    const handleClose = () => {
                        setAnchorEl(null);
                    };

                    return (
                        <FaiBox display="flex" gap={1}>
                            <FaiBox 
                                component={"img"} 
                                src={EditIcon} 
                                height="29px" 
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleEditClick(row.original.id)}
                            />
                            <DeleteIcon 
                                sx={{ 
                                    cursor: 'pointer', 
                                    color: 'error.main',
                                    height: '29px',
                                    width: 'auto'
                                }}
                                onClick={() => handleDeleteClick(row.original.id)}
                            />
                            <IconButton
                                onClick={handleClick}
                                size="small"
                                sx={{ ml: 2 }}
                                aria-controls={open ? 'media-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                            >
                                <MoreVertIcon />
                            </IconButton>
                            <Menu
                                anchorEl={anchorEl}
                                id="media-menu"
                                open={open}
                                onClose={handleClose}
                                onClick={handleClose}
                                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            >
                                <MenuItem onClick={() => handleAddMedia(row.original.id)}>Add Media</MenuItem>
                                <MenuItem onClick={() => handleDeleteMedia(row.original.id)}>Delete Media</MenuItem>
                            </Menu>
                        </FaiBox>
                    );
                }
            },
        ],
        rows: sales
    };

    return (
        <>
            <FaiBox>
                <FaiBox className={"tab-title"} mb={"21px"}>
                    <FaiTypography variant={"h5"} fontWeight={typography.fontWeightMedium}>
                        Ongoing Sales
                    </FaiTypography>
                </FaiBox>
                <FaiBox bgColor={palette.white.main} p={"15px"}
                    borderRadius={borders.borderRadius.md}>
                    {loading ? (
                        <FaiTypography>Loading sales...</FaiTypography>
                    ) : (
                        <DataTable
                            table={tableData}
                            pagination={{
                                variant: "contained",
                                color: "primary",
                            }}
                        />
                    )}
                </FaiBox>
            </FaiBox>

            <EditSaleModal
                open={editModalOpen}
                onClose={handleCloseEditModal}
                saleId={selectedSaleId}
                onSaleUpdated={handleSaleUpdated}
            />

            <MediaUploadModal
                open={mediaUploadOpen}
                onClose={handleCloseMediaUpload}
                saleId={selectedSaleForMedia || ''}
                onSuccess={handleMediaUploadSuccess}
            />

            <Dialog
                open={deleteConfirmOpen}
                onClose={handleCloseDeleteConfirm}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this sale? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <FaiButton onClick={handleCloseDeleteConfirm} color="primary">
                        Cancel
                    </FaiButton>
                    <FaiButton 
                        onClick={handleConfirmDelete} 
                        color="error" 
                        variant="contained"
                        disabled={deleteLoading}
                    >
                        {deleteLoading ? 'Deleting...' : 'Delete'}
                    </FaiButton>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbarSeverity} 
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}

export default OngoingSales;
